import React, { useState } from "react";
import { Mail, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { LoginResponse } from "../types";
import { useTranslation } from "react-i18next";
import { login, forgotPassword } from "../api/auth";
import { toast } from "sonner";

interface LoginProps {
  onLogin: (user: LoginResponse) => void;
  onSwitchMode: (mode: "login" | "register" | "forgotPassword") => void;
}

const Login: React.FC<LoginProps> = ({ onLogin, onSwitchMode }) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [view, setView] = useState<"login" | "forgotPassword">("login");
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const result = await login(formData);

    setIsLoading(false);

    if (result.error) {
      console.error("Login failed:", result.error);
      toast.error(`Login failed: ${result.error.message}`);
    } else if (result.data) {
      console.log("Login successful:", result.data);
      onLogin(result.data);
    }
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotPasswordEmail) {
      toast.error(
        t(
          "auth.enterEmailForPasswordReset",
          "Please enter your email address to reset your password."
        )
      );
      return;
    }
    setIsLoading(true);
    const result = await forgotPassword({ email: forgotPasswordEmail });
    setIsLoading(false);

    if (result.error) {
      console.error("Forgot password failed:", result.error);
      toast.error(result.error.message);
    } else {
      toast.success(t("auth.forgotPasswordSuccess"));
      setView("login");
      onSwitchMode("login");
    }
  };

  if (view === "forgotPassword") {
    return (
      <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("auth.emailAddress", "Email Address")}
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="email"
              name="email"
              value={forgotPasswordEmail}
              onChange={(e) => setForgotPasswordEmail(e.target.value)}
              required
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
              placeholder={t("auth.yourEmail", "your.email@example.com")}
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={isLoading}
          className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 shadow-sm hover:shadow-md ${
            isLoading
              ? "bg-gray-400 text-gray-200 cursor-not-allowed"
              : "bg-orange-500 text-white hover:bg-orange-600"
          }`}
        >
          {isLoading ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>{t("auth.sendingResetLink", "Sending...")}</span>
            </div>
          ) : (
            t("auth.sendResetLink")
          )}
        </button>
        <div className="text-center">
          <button
            type="button"
            onClick={() => {
              setView("login");
              onSwitchMode("login");
            }}
            className=" inline-flex items-center text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-orange-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t("auth.backToLogin")}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Email */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t("auth.emailAddress")}
        </label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
            placeholder={t("auth.yourEmail")}
          />
        </div>
      </div>

      {/* Password */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t("auth.password")}
        </label>
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            required
            className="w-full pl-4 pr-12 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white text-sm"
            placeholder={t("auth.passwordPlaceholder")}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            {showPassword ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      <div className="text-right">
        <button
          type="button"
          onClick={() => {
            setForgotPasswordEmail(formData.email);
            setView("forgotPassword");
            onSwitchMode("forgotPassword");
          }}
          className="text-sm text-orange-500 hover:underline"
        >
          {t("auth.forgotPassword")}
        </button>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading}
        className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 shadow-sm hover:shadow-md ${
          isLoading
            ? "bg-gray-400 text-gray-200 cursor-not-allowed"
            : "bg-orange-500 text-white hover:bg-orange-600"
        }`}
      >
        {isLoading ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span>{t("auth.connecting")}</span>
          </div>
        ) : (
          t("auth.login")
        )}
      </button>
    </form>
  );
};

export default Login;
